import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ChevronLeft, ChevronRight, X, Truck, ShieldCheck, Bitcoin } from "lucide-react";
import { API, carTitle, formatKm, formatPrice } from "../lib/company";
import { CarPhoto } from "../components/CarCard";
import { ContactButtons } from "../components/ContactButtons";
import { tv, useT } from "../lib/i18n";

const FIELDS = ["marca", "modello", "anno", "km", "colore", "carburante", "cambio", "potenza", "targa", "telaio"];
const PERK_ICONS = [Truck, ShieldCheck, Bitcoin];

const Lightbox = ({ photos, index, setIndex, onClose }) => {
  useEffect(() => {
    const k = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % photos.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [photos.length, onClose, setIndex]);
  return (
    <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center" data-testid="lightbox">
      <button onClick={onClose} className="absolute top-5 right-5 text-white p-2" data-testid="lightbox-close-btn" aria-label="Chiudi"><X className="w-7 h-7" /></button>
      <button onClick={() => setIndex((i) => (i - 1 + photos.length) % photos.length)} className="absolute left-3 md:left-8 text-white p-3 rounded-full bg-white/10 hover:bg-white/20" data-testid="lightbox-prev-btn" aria-label="Precedente"><ChevronLeft /></button>
      <div className="w-full h-full max-w-6xl max-h-[85vh] px-16"><CarPhoto path={photos[index]} alt="" className="!object-contain" /></div>
      <button onClick={() => setIndex((i) => (i + 1) % photos.length)} className="absolute right-3 md:right-8 text-white p-3 rounded-full bg-white/10 hover:bg-white/20" data-testid="lightbox-next-btn" aria-label="Successiva"><ChevronRight /></button>
      <p className="absolute bottom-6 font-mono text-sm text-zinc-300">{index + 1} / {photos.length}</p>
    </div>
  );
};

export default function CarDetail() {
  const t = useT();
  const d = t.detail;
  const { id } = useParams();
  const [car, setCar] = useState(undefined);
  const [idx, setIdx] = useState(0);
  const [lb, setLb] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/cars/${id}`).then((r) => setCar(r.data)).catch(() => setCar(null));
  }, [id]);

  if (car === undefined) return <div className="min-h-screen pt-32 max-w-[1400px] mx-auto px-5"><div className="aspect-[16/9] rounded-[28px] bg-[#E9E6DE] animate-pulse" /></div>;
  if (car === null) return (
    <div className="min-h-screen pt-40 text-center px-5" data-testid="car-not-found">
      <p className="font-display text-4xl font-semibold">{d.notFound}</p>
      <Link to="/" className="mt-6 inline-block underline">{d.backHome}</Link>
    </div>
  );

  const photos = car.foto ?? [];
  const specs = FIELDS.filter((k) => car[k] !== null && car[k] !== undefined && car[k] !== "");
  const title = carTitle(car, t.card.fallback);
  const msg = `${d.interest} ${title} (${formatPrice(car.prezzo)}). ${window.location.href}`;

  return (
    <main className="pt-[96px] md:pt-[112px] pb-24" data-testid="car-detail-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--ink-2)] hover:text-[var(--red)]" data-testid="car-back-link"><ArrowLeft className="w-4 h-4" /> {d.back}</Link>
        <div className="mt-6 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7 xl:col-span-8">
            <motion.button initial={{ clipPath: "inset(10% 10% 10% 10% round 28px)" }} animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => photos.length && setLb(true)} className="relative block w-full aspect-[4/3] overflow-hidden rounded-[28px] bg-[var(--ink)]" data-testid="car-main-photo">
              <CarPhoto path={photos[idx]} alt={title} />
              {car.venduto && <span className="absolute top-5 left-5 rounded-full bg-[var(--red)] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5">{t.card.sold}</span>}
              {photos.length > 1 && <span className="absolute bottom-5 right-5 rounded-full bg-black/55 backdrop-blur text-white font-mono text-xs px-3 py-1.5">{idx + 1} / {photos.length}</span>}
            </motion.button>
            {photos.length > 1 && (
              <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-3" data-testid="car-thumbnails">
                {photos.map((p, i) => (
                  <button key={p} onClick={() => setIdx(i)} data-testid={`car-thumb-${i}`}
                    className={`aspect-[4/3] overflow-hidden rounded-xl ring-2 transition-[box-shadow,opacity] ${i === idx ? "ring-[var(--red)]" : "ring-transparent opacity-70 hover:opacity-100"}`}>
                    <CarPhoto path={p} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-28 self-start">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--red)]">{[car.anno, car.km != null ? formatKm(car.km) : null].filter(Boolean).join(" · ") || "YOUCARS · Lecco"}</p>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] leading-[0.98]" data-testid="car-title">{title}</h1>
            <p className="mt-6 font-display text-4xl md:text-5xl font-semibold" data-testid="car-detail-price">{formatPrice(car.prezzo)}</p>
            <div className="mt-8"><ContactButtons prefix="car" message={msg} subject={`${d.subject} ${title}`} /></div>
            {specs.length > 0 && (
              <dl className="mt-8 rounded-[22px] border border-[var(--line)] bg-white divide-y divide-[var(--line)]" data-testid="car-specs">
                {specs.map((k) => (
                  <div key={k} className="flex justify-between gap-4 px-5 py-3.5 text-sm">
                    <dt className="text-[var(--muted)]">{d.fields[k]}</dt>
                    <dd className={`font-semibold text-right ${["targa", "telaio"].includes(k) ? "font-mono break-all" : ""}`}>{k === "km" ? formatKm(car.km) : tv(t, car[k])}</dd>
                  </div>
                ))}
              </dl>
            )}
            <ul className="mt-6 space-y-3 text-sm text-[var(--ink-2)]">
              {d.perks.map((p, i) => { const Icon = PERK_ICONS[i]; return (
                <li key={i} className="flex gap-3"><Icon className="w-5 h-5 text-[var(--red)] shrink-0" /> {p}</li>
              ); })}
            </ul>
          </aside>
        </div>
        {car.descrizione && (
          <section className="mt-16 max-w-3xl" data-testid="car-description">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--red)]">{d.desc}</p>
            <p className="mt-4 text-base md:text-lg leading-relaxed whitespace-pre-line text-[var(--ink-2)]">{car.descrizione}</p>
          </section>
        )}
      </div>
      {lb && <Lightbox photos={photos} index={idx} setIndex={setIdx} onClose={() => setLb(false)} />}
    </main>
  );
}
